import React, { useEffect, useMemo, useState } from 'react';
import { FinancialValue } from '../shared/FinancialValue';
import { StateChip } from '../shared/StateChip';
import { HonestState } from '../workspace/primitives/HonestState';
import PropertyElectionBand from './PropertyElectionBand';
import PropertyCapConfirmCard from './PropertyCapConfirmCard';
import type {
  PropertyReconciliation,
  MatchDetail,
  VarianceDetail,
  LineSnapshot,
  Summary,
  Meta,
  Readiness,
  ReadinessCheck,
  SketchArea,
  SketchAreas,
  MatchCandidate,
  MatchOverlayView,
  AiSuggestion,
  AiSuggestionsProgress,
  SuggestionDecisionItem,
} from '../../services/propertyReconciliationApi';
import { patchMatchOverlay, getMatchOverlay } from '../../services/propertyReconciliationApi';
import {
  generateAiSuggestions,
  getAiSuggestionsProgress,
  listAiSuggestions,
  decideAiSuggestions,
  getSuggestionOverlay,
} from '../../services/propertyReconciliationApi';
import {
  sumCents,
  pairDeltaCents,
  centsToDollars,
  humanizeLoc,
  wordDiff,
  sharedKeywords,
  topKeywords,
  normToken,
  type DiffSpan,
} from './reconciliationMoney';

/**
 * ReconciliationWorkbench — the read-only property line-item matching workbench (ticket 174).
 *
 * A PORT of the owner's WTFK matching UX onto the ClaimGenius dark/orange system: walk the
 * location hierarchy (the matcher's DEFAULT_HIERARCHY_FIELDS spine: phase → building → unit →
 * room → component, tiers auto-HIDDEN when blank on both estimates), read every carrier/expert
 * pair side-by-side, and — the product's whole point — SEE why the matcher bridged two lines
 * whose contractor/adjuster wording differs (word-level name-mismatch diff + confidence).
 *
 * The deterministic result is READ-ONLY: no verify / accept / create-match on the 97/699 baseline,
 * no election here. The ONE human write is the reversible Match/No-match OVERLAY over the propose-only
 * candidate tray (MATCH-S3) — stored on the draft, applied to NOTHING, never touching a count, a
 * variance, or the L_PROP election. Binds ONLY to the deployed GET /result payload (a=carrier,
 * b=expert). MONEY-FENCE: every dollar renders VERBATIM from the engine strings; the one permitted
 * client money-math is the per-pair Δ in integer cents (reconciliationMoney). `net` / `gross` are
 * verbatim from summary — never summed, never relabeled as "the estimates differ by $X".
 */

const CARD_FRAME =
  'rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] overflow-hidden';
const SECTION_HEADER =
  'text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-muted)]';

// ── Location cascade spine (matcher DEFAULT_HIERARCHY_FIELDS). phase/component are the
// matcher's internal tiers, NOT projected onto /result line snapshots → their accessors
// return null → the tier auto-hides (the "hide a tier when every line on both sides is
// blank; don't hardcode unit away" rule). If the backend later emits them, they appear. ──
type CascadeKey = 'phase' | 'building' | 'location_unit' | 'room' | 'component';
const CASCADE: Array<{ key: CascadeKey; label: string; get: (l: LineSnapshot) => string | null | undefined }> = [
  { key: 'phase', label: 'Phase', get: (l) => (l as any).phase ?? null },
  { key: 'building', label: 'Building', get: (l) => l.building },
  { key: 'location_unit', label: 'Unit', get: (l) => l.location_unit ?? null },
  { key: 'room', label: 'Room', get: (l) => l.room },
  { key: 'component', label: 'Component', get: (l) => (l as any).component ?? null },
];

type StatusFilter = 'all' | 'matched' | 'one-sided' | 'needs-review';

interface Filters {
  status: StatusFilter;
  cascade: Record<CascadeKey, string>; // 'ALL' or a raw value
  coverage: string; // coverage_section chip: 'ALL' or a value
  matchType: string; // 'ALL' or a match_type
  activity: string; // 'ALL' or an action code
  search: string;
}

const EMPTY_CASCADE: Record<CascadeKey, string> = {
  phase: 'ALL', building: 'ALL', location_unit: 'ALL', room: 'ALL', component: 'ALL',
};

// ── Auto-reconciliation router status (WTFK): a pair the engine auto-accepted leaves the review
// pile. Absent status (older payloads) → treated as needs-review so nothing is silently auto-hidden. ──
function isAutoApproved(m: MatchDetail): boolean {
  // Belt-and-braces with the backend router: an engine-flagged pair (requires_review) never counts
  // as auto-accepted even if a stale/mis-stamped payload says so — a flagged pair stays in the queue.
  return (m.auto_reconciliation_status ?? '') === 'auto_approved' && m.requires_review !== true;
}

// ── breadcrumb from a line's location fields (humanized, presentation-only) ──
function breadcrumb(line: LineSnapshot | undefined): string {
  if (!line) return '';
  return [line.building, line.location_unit, line.room]
    .map((x) => humanizeLoc(x))
    .filter(Boolean)
    .join(' › ');
}

// ── Word-diff renderer: shared words recede; differing words take the steel-blue diff
// tint + weight 500 + dotted underline + soft wash (hue is never the sole channel). ──
function DiffText({ spans }: { spans: DiffSpan[] }) {
  return (
    <>
      {spans.map((s, i) => {
        if (s.kind === 'space') return <React.Fragment key={i}>{s.text}</React.Fragment>;
        if (s.kind === 'diff') {
          return (
            <span
              key={i}
              style={{
                color: 'var(--color-diff-divergent)',
                fontWeight: 500,
                textDecoration: 'underline dotted',
                textUnderlineOffset: '2px',
                textDecorationColor: 'color-mix(in srgb, var(--color-diff-divergent) 70%, transparent)',
                backgroundColor: 'var(--color-diff-divergent-bg)',
                borderRadius: '2px',
                padding: '0 1px',
              }}
            >
              {s.text}
            </span>
          );
        }
        return (
          <span key={i} style={{ color: 'var(--color-diff-common)' }}>
            {s.text}
          </span>
        );
      })}
    </>
  );
}

// ── Small presentational bits ──
function ReviewDot() {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--color-needs-human-text)]"
      aria-label="requires review"
      title="requires review"
    />
  );
}

function Pill({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.06em] font-mono text-[var(--color-text-secondary)] border border-[var(--color-hairline)] rounded-sm px-1.5 py-px"
    >
      {children}
    </span>
  );
}

// Confidence pill + hueless bar (a confident match is not a celebration color).
function ConfidenceMeter({ confidence }: { confidence: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(confidence * 100)));
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] font-mono tabular-nums text-[var(--color-text-secondary)]">
        {pct}% CONF
      </span>
      <span className="inline-block h-[3px] w-12 rounded-sm bg-[var(--color-hairline)] overflow-hidden align-middle">
        <span
          className="block h-full bg-[var(--color-text-primary)]"
          style={{ width: `${pct}%`, opacity: 0.85 }}
        />
      </span>
    </span>
  );
}

// Metadata-agreement dot: filled = agree (recede), hollow ring = disagree. Never orange/red.
function AgreeDot({ label, agree }: { label: string; agree: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={
          agree
            ? { backgroundColor: 'var(--color-text-primary)', opacity: 0.4 }
            : { boxShadow: 'inset 0 0 0 1px var(--color-text-secondary)' }
        }
        aria-hidden
      />
      <span
        className="text-[9px] uppercase tracking-[0.06em] font-mono"
        style={{ color: agree ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}
      >
        {label}
      </span>
    </span>
  );
}

// Financial mini-grid for one line inside the expanded comparison (verbatim strings only).
function LineFinancials({ line }: { line: LineSnapshot }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 mt-1.5">
      <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono">
        Qty {line.quantity} · {line.unit}
      </span>
      <span />
      <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono">
        Extended
      </span>
      <FinancialValue value={line.extended_price} size="md" cents />
    </div>
  );
}

// ── Cascade / chip filter primitives ──
function CascadeSelect({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: Array<{ raw: string; label: string; count: number }>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 min-w-[7.5rem]">
      <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[12px] bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-sm px-2 h-8 text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
      >
        <option value="ALL">All {label}s</option>
        {options.map((o) => (
          <option key={o.raw} value={o.raw}>
            {o.label} ({o.count})
          </option>
        ))}
      </select>
    </label>
  );
}

function ChipGroup({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: Array<{ raw: string; label: string; count: number }>;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mr-0.5">
        {label}
      </span>
      {options.map((o) => {
        const active = value === o.raw;
        return (
          <button
            key={o.raw}
            type="button"
            onClick={() => onChange(active ? 'ALL' : o.raw)}
            className={[
              'text-[10px] uppercase tracking-[0.06em] font-mono rounded-sm px-1.5 h-6 border',
              active
                ? 'border-[var(--color-text-secondary)] text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)]'
                : 'border-[var(--color-hairline)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
            ].join(' ')}
            aria-pressed={active}
          >
            {o.label} <span className="text-[var(--color-text-muted)]">{o.count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── U2 · Coverage-lane + responsibility (condo scope allocation) presentation maps ──
// The scope engine already classified each one-sided line onto a coverage lane + a responsibility
// role (backend property_report._variances). These are PRESENTATION labels only — no money, no
// re-classification. 'unknown' is a real "engine could not classify — flagged for review" state,
// rendered honestly (never hidden, never guessed into a bucket).
const LANE_LABEL: Record<string, string> = {
  roof: 'Roof',
  building_envelope: 'Building Envelope',
  unit_interior: 'Unit Interior',
  association_common: 'Association / Common',
  unknown: 'Unclassified',
};
// Stable tie-break order when two lanes share a subtotal (primary sort is $ desc).
const LANE_ORDER = ['roof', 'building_envelope', 'unit_interior', 'association_common', 'unknown'];
const OWNERSHIP_LABEL: Record<string, string> = {
  association: 'ASSOCIATION',
  unit_owner: 'UNIT-OWNER',
  unknown: 'UNKNOWN',
};

// Σ abs_amount over a set of scope variances, in INTEGER CENTS (the money-fence's permitted
// client math — same discipline as pairDeltaCents / sumCents). null if ANY value is unparseable,
// so a bad engine string surfaces as FinancialValue's NOT-RUN chip, never a silent under-count.
function laneAbsCents(vars: VarianceDetail[]): number | null {
  let total = 0;
  for (const v of vars) {
    const s = v.abs_amount;
    if (s == null || s === '') return null;
    const n = Number(String(s).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n)) return null;
    total += Math.round(n * 100);
  }
  return total;
}

// A one-sided line's ownership tag (who pays: Association / Unit-Owner). BORDERLESS `RESP · X` text,
// NOT a chip (K3 gate): SideTag = which estimate, OwnershipTag = who pays, lane = where — three
// taxonomies. Side vs ownership is disambiguated by SILHOUETTE (bordered chip vs prefixed text) so an
// unprefixed "ASSOCIATION" beside "EXPERT ONLY" can't read as a third side. `unknown` is a real engine
// verdict (a classification failure) → RESP · UNKNOWN one ink-tier brighter (the hue-free attention
// lever: muted = settled metadata, brighter = unresolved). A MISSING field (null) → omit entirely.
function OwnershipTag({ responsibility, basis }: { responsibility?: string | null; basis?: string | null }) {
  if (!responsibility) return null; // absent field ≠ 'unknown' verdict — no data, no tag
  const label = OWNERSHIP_LABEL[responsibility] ?? responsibility.replace(/_/g, '-').toUpperCase();
  const unknown = responsibility === 'unknown';
  // 'lane_default' = inferred from a per-claim condo convention, not read from the estimate — mark it
  // so a court reader distinguishes an inferred allocation from one carried on the line ('·def').
  const inferred = basis === 'lane_default';
  return (
    <span
      title={inferred
        ? `responsibility: ${responsibility} (inferred from coverage lane — condo convention, not read from the estimate)`
        : `responsibility: ${responsibility}`}
      className={[
        'text-[9px] uppercase tracking-[0.08em] font-mono whitespace-nowrap',
        unknown ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-muted)]',
      ].join(' ')}
    >
      RESP · {label}
      {inferred && <span className="text-[var(--color-text-muted)]"> ·def</span>}
    </span>
  );
}

// ── A · Roof sketch measurements (PROPOSE-ONLY, read-only) ──
// Verbatim roof geometry lifted from the estimate sketch pages (backend sketch_adapter). Shown next
// to the ROOF one-sided lines so an expert can compare carrier vs expert roof measurement WITHOUT it
// touching the reconciliation math — nothing here is editable, elected, or summed into a dollar. A
// roof face is a sketch area carrying number_of_squares; room areas are not shown in this roof panel.
const _ROOF_COLS: Array<{ key: keyof SketchArea; label: string }> = [
  { key: 'number_of_squares', label: 'SQ' },
  { key: 'ridge_length_lf', label: 'RIDGE' },
  { key: 'hip_length_lf', label: 'HIP' },
  { key: 'valley_length_lf', label: 'VALLEY' },
];

function RoofSketchSide({ label, faces }: { label: string; faces: SketchArea[] }) {
  if (faces.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono mb-1.5">
        {label} · {faces.length} {faces.length === 1 ? 'face' : 'faces'}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_repeat(4,44px)] gap-x-3 gap-y-1 items-baseline">
        <span className="text-[9px] uppercase tracking-[0.06em] text-[var(--color-text-muted)]">Face</span>
        {_ROOF_COLS.map((c) => (
          <span key={c.label} className="text-[9px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] text-right font-mono">{c.label}</span>
        ))}
        {faces.map((f) => (
          <React.Fragment key={f.area_id}>
            <span className="text-[12px] text-[var(--color-text-secondary)] truncate" title={f.name}>{f.name}</span>
            {_ROOF_COLS.map((c) => {
              const val = f[c.key] as string | null | undefined;
              return (
                <span key={c.label} className="text-[12px] font-mono tabular-nums text-right text-[var(--color-text-primary)]">
                  {val == null || val === '' ? '—' : val}
                </span>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function RoofSketchPanel({ sketch }: { sketch?: SketchAreas | null }) {
  const roofFaces = (arr?: SketchArea[]) => (arr ?? []).filter((a) => a.number_of_squares != null && a.number_of_squares !== '');
  const carrier = roofFaces(sketch?.carrier);
  const expert = roofFaces(sketch?.expert);
  if (carrier.length === 0 && expert.length === 0) return null;
  return (
    <div className="px-5 py-3 border-t border-[var(--color-hairline)] bg-[var(--color-bg-elevated)]">
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-secondary)]">Sketch measurements</span>
        <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono border border-[var(--color-hairline)] rounded-sm px-1 py-px">proposed · read-only</span>
        <span className="text-[10px] text-[var(--color-text-muted)] font-mono">verbatim from the estimate sketch pages · not in the reconciliation math</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <RoofSketchSide label="Carrier" faces={carrier} />
        <RoofSketchSide label="Expert" faces={expert} />
      </div>
      <div className="text-[9px] text-[var(--color-text-muted)] font-mono mt-2">SQ = squares · RIDGE/HIP/VALLEY in LF · blank = —</div>
    </div>
  );
}

// ── S2 · Suggested matches (PROPOSE-ONLY candidate tray) ──
// A faithful port of the owner's WTFK "AI Suggested Matches" list/card onto the ClaimGenius law:
// residue near-misses the matcher scored BELOW its auto-match cutoff (band [floor, 84)), so a human
// can adjudicate Match / No-match. MONEY-FENCE: candidates are applied to NOTHING — the deterministic
// 97/699/$ baseline never moves. The backend generates them PER location bucket, so a pair here can
// never cross a building/unit/room. This slice renders the tray + records a decision in SESSION-LOCAL
// state only (the "unsaved" chip is honest); S3 persists Match/No-match to a separate reversible
// overlay bound to input_hash+run_at and derives the "with N matches" view — the frozen baseline is
// still never mutated.
type CandidateDecision = 'matched' | 'rejected';

function pairKey(a: string, b: string): string {
  // client map key; the separator is NUL so a "|" inside an id can never collide (mirrors the
  // server-side keying discipline Fable specced for the S3 overlay).
  return `${a} ${b}`;
}
function candKey(c: MatchCandidate): string {
  return pairKey(c.a_item_id, c.b_item_id);
}

function SuggestedSide({ label, desc, line }: { label: string; desc: string; line?: LineSnapshot }) {
  return (
    <div className="min-w-0 border border-[var(--color-hairline)] rounded-sm p-2.5 bg-[var(--color-bg-elevated)]">
      <div className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono mb-1">{label}</div>
      <div className="text-[12px] text-[var(--color-text-primary)] leading-snug break-words" title={desc}>{desc}</div>
      <div className="text-[10px] text-[var(--color-text-muted)] font-mono mt-1">
        {[line?.source_code, breadcrumb(line)].filter(Boolean).join(' · ') || '—'}
      </div>
      {line && <LineFinancials line={line} />}
    </div>
  );
}

function SuggestedMatchCard({
  cand, aLine, bLine, decision, saved, busy, onDecide,
}: {
  cand: MatchCandidate;
  aLine?: LineSnapshot;
  bLine?: LineSnapshot;
  decision: CandidateDecision | null;
  saved: boolean;
  busy: boolean;
  onDecide: (d: CandidateDecision) => void;
}) {
  const [open, setOpen] = useState(false);
  const diff = wordDiff(cand.a_description, cand.b_description);
  const shared = sharedKeywords(cand.a_description, cand.b_description, 4);
  const decided = decision != null;
  return (
    <div
      className="px-5 py-2.5 border-t border-[var(--color-hairline)]"
      style={decided ? { opacity: 0.72 } : undefined}
    >
      {/* header row: fuzzy glyph + differing-wording diff + score + expand */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] leading-snug">
            <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5">≈</span>
            <DiffText spans={diff.a} />
          </div>
          <div className="text-[13px] leading-snug mt-0.5">
            <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5">≈</span>
            <DiffText spans={diff.b} />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ConfidenceMeter confidence={cand.confidence} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] h-6 px-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
            aria-expanded={open}
          >
            {open ? 'Less ▴' : 'Detail ▾'}
          </button>
        </div>
      </div>

      {/* metadata agreement row (recede when agreeing; never rainbow) */}
      <div className="flex items-center gap-3 flex-wrap mt-1.5">
        <AgreeDot label={cand.category_match ? 'same trade' : 'trade differs'} agree={cand.category_match} />
        {cand.quantity_ok != null && (
          <AgreeDot label={cand.quantity_ok ? 'qty agrees' : 'qty differs'} agree={cand.quantity_ok} />
        )}
        {!cand.actions_ok && (
          <Pill title={`carrier ${cand.a_actions.join('+') || '—'} vs expert ${cand.b_actions.join('+') || '—'}`}>
            action differs
          </Pill>
        )}
        {shared.length > 0 && (
          <span className="text-[9px] uppercase tracking-[0.06em] font-mono text-[var(--color-text-muted)]">
            shared: {shared.join(' · ')}
          </span>
        )}
      </div>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2.5">
          <SuggestedSide label="Carrier" desc={cand.a_description} line={aLine} />
          <SuggestedSide label="Expert" desc={cand.b_description} line={bLine} />
        </div>
      )}

      {/* Match / No-match actions — both always visible, the human's choice highlighted; a chip shows
          whether the decision persisted to the overlay (saved) or is local-only (unsaved). No dollar. */}
      <div className="flex items-center justify-end gap-2 mt-2.5">
        {decided && (
          <StateChip
            state={busy ? 'system-active' : saved ? 'verified' : 'warning'}
            text={busy ? 'SAVING' : saved ? 'SAVED' : 'UNSAVED'}
          />
        )}
        <button
          type="button"
          disabled={busy}
          aria-pressed={decision === 'rejected'}
          onClick={() => onDecide('rejected')}
          className={[
            'text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3 border',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] disabled:opacity-50',
            decision === 'rejected'
              ? 'border-[var(--color-border)] text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)]'
              : 'border-[var(--color-hairline)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          ].join(' ')}
        >
          No match
        </button>
        <button
          type="button"
          disabled={busy}
          aria-pressed={decision === 'matched'}
          onClick={() => onDecide('matched')}
          className={[
            'text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-bg-surface)] disabled:opacity-50',
            decision === 'matched'
              ? 'text-[var(--color-on-accent)] bg-[var(--color-brand)]'
              : 'text-[var(--color-needs-human-text)] border border-[var(--color-needs-human-tint-bd)] hover:bg-[var(--color-needs-human-tint)]',
          ].join(' ')}
        >
          Match
        </button>
      </div>
    </div>
  );
}

interface DecisionState { decision: CandidateDecision; saved: boolean }

function SuggestedMatchesPanel({
  candidates, lineItems, reportId, inputHash, runAt,
}: {
  candidates?: MatchCandidate[] | null;
  lineItems: Record<string, LineSnapshot>;
  reportId?: string;
  inputHash?: string;
  runAt?: string;
}) {
  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [view, setView] = useState<MatchOverlayView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const canPersist = !!(reportId && inputHash && runAt);

  // Hydrate persisted, NON-stale, NON-orphaned decisions on load (+ the derived preview). A failure
  // (offline harness, 404) is non-fatal — the tray still renders and decisions stay session-local.
  useEffect(() => {
    let alive = true;
    if (!reportId) return;
    (async () => {
      try {
        const st = await getMatchOverlay(reportId);
        if (!alive || !st) return;
        const hydrated: Record<string, DecisionState> = {};
        for (const r of st.overlay || []) {
          if (r._stale || r._orphaned) continue;      // fail-safe: never apply a stale/orphaned record
          hydrated[pairKey(r.a_item_id, r.b_item_id)] = { decision: r.decision, saved: true };
        }
        setDecisions(hydrated);
        setView(st.overlay_view ?? null);
      } catch {
        /* non-fatal — leave the tray in session-local mode */
      }
    })();
    return () => { alive = false; };
  }, [reportId, inputHash, runAt]);

  const decide = async (c: MatchCandidate, d: CandidateDecision) => {
    const key = candKey(c);
    if (decisions[key]?.decision === d && decisions[key]?.saved) return;   // no-op re-click
    setErr(null);
    setDecisions((prev) => ({ ...prev, [key]: { decision: d, saved: false } }));
    if (!canPersist) return;                          // harness / no binding → session-local only
    setBusyKey(key);
    try {
      await patchMatchOverlay(reportId!, {
        a_item_id: c.a_item_id, b_item_id: c.b_item_id, decision: d,
        input_hash: inputHash!, run_at: runAt!,
      });
      setDecisions((prev) => ({ ...prev, [key]: { decision: d, saved: true } }));
      const st = await getMatchOverlay(reportId!);     // refresh the derived preview
      if (st) setView(st.overlay_view ?? null);
    } catch (e: any) {
      setErr(e?.status === 409
        ? 'The reconciliation changed since you loaded it — refresh and re-decide.'
        : 'Could not save that decision.');
      // keep the optimistic decision but flagged unsaved (honest), so the human sees it did not persist.
    } finally {
      setBusyKey(null);
    }
  };

  const list = candidates ?? [];
  if (list.length === 0) return null;
  const decidedCount = Object.keys(decisions).length;
  const savedCount = Object.values(decisions).filter((x) => x.saved).length;
  return (
    <div className="border-t border-[var(--color-hairline)]">
      <div className="px-5 h-9 flex items-center justify-between border-b border-[var(--color-hairline)]">
        <span className={SECTION_HEADER}>SUGGESTED MATCHES · {list.length}</span>
        <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
          {decidedCount > 0
            ? `${savedCount}/${decidedCount} saved`
            : 'below the auto-match cutoff'}
        </span>
      </div>
      <div className="px-5 py-2 border-b border-[var(--color-hairline)] bg-[var(--color-bg-elevated)]">
        <span className="text-[10px] text-[var(--color-text-muted)] font-mono leading-relaxed">
          Propose-only near-misses the matcher scored just below its auto-match cutoff — same location
          bucket, never cross-building. Not counted in the deterministic matches or variances. Match /
          No-match records to a separate reversible overlay; the frozen baseline never changes.
        </span>
      </div>
      {err && (
        <div className="px-5 py-1.5 border-b border-[var(--color-hairline)]">
          <span className="text-[10px] font-mono text-[var(--color-blocker)]">{err}</span>
        </div>
      )}
      {list.map((c) => {
        const key = candKey(c);
        const st = decisions[key];
        return (
          <SuggestedMatchCard
            key={key}
            cand={c}
            aLine={lineItems[`line_item:${c.a_item_id}`]}
            bLine={lineItems[`line_item:${c.b_item_id}`]}
            decision={st?.decision ?? null}
            saved={!!st?.saved}
            busy={busyKey === key}
            onDecide={(d) => decide(c, d)}
          />
        );
      })}
      {view && view.matched_count > 0 && (
        <div className="px-5 py-2.5 border-t border-[var(--color-hairline)] flex items-baseline justify-between gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            Overlay preview · {view.matched_count} matched
          </span>
          <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
            Σ|Δ| {view.gross_abs_delta_rcv} — preview only, not a payable · not applied to the reconciliation
          </span>
        </div>
      )}
    </div>
  );
}

// ── WTFK-D · AI Suggested Matches (async MiniLM clustering) ──
// A SECOND, separate tray from the [floor,84) fuzzy candidate tray above. Suggestions come from an
// async clustering job on the ISOLATED matching worker (POST generate → poll progress every 2000 ms →
// GET ranked list). MONEY-FENCE: identical discipline to S3 — Match/No-match records to a SEPARATE
// reversible draft overlay (suggestion-overlay), NEVER the reconciliation blob, NEVER the 97/699
// baseline, NEVER the fuzzy candidate overlay. Gated by the backend RECON_MATCHING_ENABLED: generate
// → 503 when off, surfaced INLINE (never toasted). Unlike the fuzzy tray this panel always renders —
// it carries the Generate CTA even when there are no suggestions yet.
type AiPhase = 'resting' | 'generating' | 'polling' | 'ready' | 'no-residue' | 'gate-off' | 'error';

// Suggestion ids round-trip to the accept path VERBATIM (never re-prefixed/stripped) — the backend
// validates the pair against exactly the shaping it emitted (_shape_suggestions). Only the DISPLAY
// line lookup is tolerant of prefixed-vs-bare, because the cluster-result id shape is confirmed only
// at WTFK-E (Fable's one open assumption); resolving both ways renders the description whichever way
// the residue-source ids land, without ever changing the id we send back.
function resolveLine(lineItems: Record<string, LineSnapshot>, id: string): LineSnapshot | undefined {
  if (!id) return undefined;
  return lineItems[id]
    ?? lineItems[`line_item:${id}`]
    ?? lineItems[id.replace(/^line_item:/, '')];
}

// 0–100 cluster match score. Mirrors ConfidenceMeter's geometry (fixed-width number + always-present
// track) so the AI cards keep the sibling tray's right-column rhythm (K3 gate #2/#3/#4): the track is
// present-but-empty for a null score, and the number sits in a fixed 2ch right-aligned slot so
// "88"/"74"/"—" align without shifting the label. The label stays "SCORE" (not "CONF") on purpose — a
// MiniLM cluster score is a different metric than the fuzzy tray's 0–1 confidence.
function ScoreMeter({ score }: { score: number | null }) {
  const has = score != null;
  const pct = has ? Math.max(0, Math.min(100, Math.round(score))) : 0;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] font-mono tabular-nums text-[var(--color-text-secondary)]">
        <span className="inline-block min-w-[2ch] text-right">{has ? pct : '—'}</span> SCORE
      </span>
      <span className="inline-block h-[3px] w-12 rounded-sm bg-[var(--color-hairline)] overflow-hidden align-middle">
        {has && <span className="block h-full bg-[var(--color-text-primary)]" style={{ width: `${pct}%`, opacity: 0.85 }} />}
      </span>
    </span>
  );
}

function AiSuggestionCard({
  sug, aLine, bLine, decision, saved, onDecide,
}: {
  sug: AiSuggestion;
  aLine?: LineSnapshot;
  bLine?: LineSnapshot;
  decision: CandidateDecision | null;
  saved: boolean;
  onDecide: (d: CandidateDecision) => void;
}) {
  const [open, setOpen] = useState(false);
  // The AI suggestion carries only ids + score + keywords; descriptions come from the projected
  // line snapshots (honest fallback when a line is not projected onto /result).
  const aDesc = aLine?.description || `carrier line ${sug.a_item_id}`;
  const bDesc = bLine?.description || `expert line ${sug.b_item_id}`;
  const haveBoth = !!aLine && !!bLine;
  const diff = haveBoth ? wordDiff(aDesc, bDesc) : null;
  const decided = decision != null;
  return (
    <div className="px-5 py-2.5 border-t border-[var(--color-hairline)]" style={decided ? { opacity: 0.72 } : undefined}>
      {/* header row: MiniLM glyph + differing-wording diff + score + expand */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {diff ? (
            <>
              <div className="text-[13px] leading-snug">
                <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5">◊</span>
                <DiffText spans={diff.a} />
              </div>
              <div className="text-[13px] leading-snug mt-0.5">
                <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5">◊</span>
                <DiffText spans={diff.b} />
              </div>
            </>
          ) : (
            <>
              <div className="text-[13px] leading-snug text-[var(--color-text-primary)] break-words">{aDesc}</div>
              <div className="text-[13px] leading-snug text-[var(--color-text-primary)] break-words mt-0.5">{bDesc}</div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ScoreMeter score={sug.score} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] h-6 px-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
            aria-expanded={open}
          >
            {open ? 'Less ▴' : 'Detail ▾'}
          </button>
        </div>
      </div>

      {/* keyword agreement row (MiniLM cluster keywords; recede, never rainbow) */}
      {sug.keywords.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap mt-1.5">
          <span className="text-[9px] uppercase tracking-[0.06em] font-mono text-[var(--color-text-muted)]">
            cluster: {sug.keywords.slice(0, 6).join(' · ')}
          </span>
        </div>
      )}

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2.5">
          <SuggestedSide label="Carrier" desc={aDesc} line={aLine} />
          <SuggestedSide label="Expert" desc={bDesc} line={bLine} />
        </div>
      )}

      {/* Match / No-match — local-first; the batch save below persists all pending decisions at once. */}
      <div className="flex items-center justify-end gap-2 mt-2.5">
        {decided && (
          <StateChip state={saved ? 'verified' : 'warning'} text={saved ? 'SAVED' : 'UNSAVED'} />
        )}
        <button
          type="button"
          aria-pressed={decision === 'rejected'}
          onClick={() => onDecide('rejected')}
          className={[
            'text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3 border',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
            decision === 'rejected'
              ? 'border-[var(--color-border)] text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)]'
              : 'border-[var(--color-hairline)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          ].join(' ')}
        >
          No match
        </button>
        <button
          type="button"
          aria-pressed={decision === 'matched'}
          onClick={() => onDecide('matched')}
          className={[
            'text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-bg-surface)]',
            decision === 'matched'
              ? 'text-[var(--color-on-accent)] bg-[var(--color-brand)]'
              : 'text-[var(--color-needs-human-text)] border border-[var(--color-needs-human-tint-bd)] hover:bg-[var(--color-needs-human-tint)]',
          ].join(' ')}
        >
          Match
        </button>
      </div>
    </div>
  );
}

function AiSuggestionsPanel({
  reportId, lineItems, inputHash, runAt,
}: {
  reportId?: string;
  lineItems: Record<string, LineSnapshot>;
  inputHash?: string;
  runAt?: string;
}) {
  const [phase, setPhase] = useState<AiPhase>('resting');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<AiSuggestionsProgress | null>(null);
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({});
  const [batchBusy, setBatchBusy] = useState(false);
  const [view, setView] = useState<MatchOverlayView | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const canPersist = !!(reportId && inputHash && runAt);

  // Mount: hydrate any existing latest-job suggestions + previously-saved overlay decisions/preview
  // (resumable across reloads). All reads are non-fatal — offline harness / gate-off / 404 → stay
  // resting so the Generate CTA is always available.
  useEffect(() => {
    let alive = true;
    if (!reportId) return;
    (async () => {
      try {
        const st = await getSuggestionOverlay(reportId);
        if (alive && st) {
          const hydrated: Record<string, DecisionState> = {};
          for (const r of st.overlay || []) {
            if (r._stale || r._orphaned) continue;   // never surface a stale/orphaned decision
            hydrated[pairKey(r.a_item_id, r.b_item_id)] = { decision: r.decision, saved: true };
          }
          setDecisions(hydrated);
          setView(st.overlay_view ?? null);
        }
      } catch { /* non-fatal */ }
      try {
        const listed = await listAiSuggestions(reportId);   // latest job for this report
        if (alive && listed && listed.count > 0 && listed.job_id) {
          setSuggestions(listed.suggestions);
          setJobId(listed.job_id);
          setPhase('ready');
        }
      } catch { /* non-fatal — stay resting */ }
    })();
    return () => { alive = false; };
  }, [reportId, inputHash, runAt]);

  // Progress poll (2000 ms) while a job is clustering. On COMPLETE, load the ranked list; on ERROR,
  // surface which batches failed. Interval is torn down on unmount / phase change.
  useEffect(() => {
    if (phase !== 'polling' || !jobId || !reportId) return;
    let alive = true;
    const tick = async () => {
      try {
        const p = await getAiSuggestionsProgress(reportId, jobId);
        if (!alive) return;
        setProgress(p);
        if (p.status === 'COMPLETE') {
          const listed = await listAiSuggestions(reportId, jobId);
          if (!alive) return;
          setSuggestions(listed.suggestions);
          setPhase('ready');
        } else if (p.status === 'ERROR') {
          setErr(`Clustering failed on ${p.error_batches?.length ?? 0} batch(es). Regenerate to retry.`);
          setPhase('error');
        }
      } catch {
        if (!alive) return;
        setErr('Lost contact with the matching worker while clustering.');
        setPhase('error');
      }
    };
    tick();                                   // immediate, then every 2s
    const iv = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [phase, jobId, reportId]);

  const generate = async () => {
    if (!reportId) return;
    setErr(null); setNote(null); setProgress(null); setSuggestions([]);
    setPhase('generating');
    try {
      const gen = await generateAiSuggestions(reportId);
      if (!gen.job_id) {
        setNote(gen.note || 'No two-sided residue to cluster — every unmatched line is one-sided.');
        setPhase('no-residue');
        return;
      }
      setJobId(gen.job_id);
      setPhase('polling');
    } catch (e: any) {
      if (e?.status === 503) { setPhase('gate-off'); return; }
      setErr(e?.status === 409
        ? 'The reconciliation changed since it loaded — reload and regenerate.'
        : 'Could not start the clustering job.');
      setPhase('error');
    }
  };

  const setLocal = (sug: AiSuggestion, d: CandidateDecision) => {
    const key = pairKey(sug.a_item_id, sug.b_item_id);
    setErr(null);
    setDecisions((prev) => {
      const cur = prev[key];
      if (cur && cur.decision === d && !cur.saved) {   // toggle off an unsaved re-click
        const n = { ...prev }; delete n[key]; return n;
      }
      return { ...prev, [key]: { decision: d, saved: false } };
    });
  };

  // Pending = locally decided but not yet persisted. Rebuilt from the suggestion list so we never
  // parse the composite key (robust to any id content).
  const pendingSugs = suggestions.filter((s) => {
    const st = decisions[pairKey(s.a_item_id, s.b_item_id)];
    return st && !st.saved;
  });

  const saveAll = async () => {
    if (!canPersist || !jobId || pendingSugs.length === 0) return;
    setBatchBusy(true); setErr(null);
    try {
      const items: SuggestionDecisionItem[] = pendingSugs.map((s) => ({
        a_item_id: s.a_item_id,
        b_item_id: s.b_item_id,
        decision: decisions[pairKey(s.a_item_id, s.b_item_id)].decision,
      }));
      await decideAiSuggestions(reportId!, { job_id: jobId, input_hash: inputHash!, run_at: runAt!, items });
      setDecisions((prev) => {
        const n: Record<string, DecisionState> = {};
        for (const [k, v] of Object.entries(prev)) n[k] = { ...v, saved: true };
        return n;
      });
      const st = await getSuggestionOverlay(reportId!);   // refresh derived preview
      if (st) setView(st.overlay_view ?? null);
    } catch (e: any) {
      setErr(e?.status === 409
        ? 'The reconciliation changed since these suggestions were generated — regenerate to re-decide.'
        : e?.status === 503
          ? 'AI matching is currently disabled.'
          : 'Could not save these decisions.');
    } finally {
      setBatchBusy(false);
    }
  };

  const generating = phase === 'generating' || phase === 'polling';
  const savedCount = Object.values(decisions).filter((x) => x.saved).length;

  return (
    <div className="border-t border-[var(--color-hairline)]">
      <div className="px-5 h-9 flex items-center justify-between border-b border-[var(--color-hairline)]">
        <span className={SECTION_HEADER}>SUGGESTED MATCHES · AI{suggestions.length > 0 ? ` · ${suggestions.length}` : ''}</span>
        <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
          {phase === 'ready' && pendingSugs.length === 0 && savedCount > 0
            ? `${savedCount} saved`
            : pendingSugs.length > 0
              ? `${pendingSugs.length} pending`
              : 'MiniLM semantic clustering'}
        </span>
      </div>

      <div className="px-5 py-2 border-b border-[var(--color-hairline)] bg-[var(--color-bg-elevated)] flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[10px] text-[var(--color-text-muted)] font-mono leading-relaxed max-w-[46rem]">
          Semantic near-misses the deterministic matcher never paired, clustered by an isolated worker.
          Not counted in the deterministic matches or variances. Match / No-match records to a separate
          reversible overlay — the frozen baseline never changes.
        </span>
        {(phase === 'resting' || phase === 'ready' || phase === 'error' || phase === 'no-residue') && (
          <button
            type="button"
            onClick={generate}
            disabled={!reportId || generating}
            className="text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3 shrink-0 text-[var(--color-needs-human-text)] border border-[var(--color-needs-human-tint-bd)] hover:bg-[var(--color-needs-human-tint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] disabled:opacity-50"
          >
            {phase === 'ready' || phase === 'error' ? 'Regenerate' : 'Generate suggestions'}
          </button>
        )}
      </div>

      {/* progress bar while a clustering job runs */}
      {generating && (
        <div className="px-5 py-3 border-b border-[var(--color-hairline)]">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
              {phase === 'generating' ? 'Starting clustering job…' : 'Clustering residue…'}
            </span>
            <span className="text-[10px] font-mono tabular-nums text-[var(--color-text-secondary)]">
              {progress ? `${progress.done_batches ?? 0}/${progress.total_batches ?? '—'} batches` : '—'}
            </span>
          </div>
          <div className="h-[3px] w-full rounded-sm bg-[var(--color-hairline)] overflow-hidden">
            <span
              className="block h-full bg-[var(--color-text-primary)] transition-[width] duration-300"
              style={{
                width: `${progress && progress.total_batches
                  ? Math.round(100 * (progress.done_batches ?? 0) / Math.max(1, progress.total_batches))
                  : 8}%`,
                opacity: 0.85,
              }}
            />
          </div>
        </div>
      )}

      {phase === 'gate-off' && (
        <div className="px-5 py-3 border-b border-[var(--color-hairline)]">
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
            AI suggestion matching is currently disabled for this environment.
          </span>
        </div>
      )}

      {phase === 'no-residue' && note && (
        <div className="px-5 py-3 border-b border-[var(--color-hairline)]">
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">{note}</span>
        </div>
      )}

      {err && (
        <div className="px-5 py-1.5 border-b border-[var(--color-hairline)]">
          <span className="text-[10px] font-mono text-[var(--color-blocker)]">{err}</span>
        </div>
      )}

      {phase === 'ready' && suggestions.length === 0 && (
        <div className="px-5 py-3">
          <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
            No semantic suggestions above the clustering threshold for this residue.
          </span>
        </div>
      )}

      {phase === 'ready' && suggestions.map((s) => {
        const key = pairKey(s.a_item_id, s.b_item_id);
        const st = decisions[key];
        return (
          <AiSuggestionCard
            key={key}
            sug={s}
            aLine={resolveLine(lineItems, s.a_item_id)}
            bLine={resolveLine(lineItems, s.b_item_id)}
            decision={st?.decision ?? null}
            saved={!!st?.saved}
            onDecide={(d) => setLocal(s, d)}
          />
        );
      })}

      {phase === 'ready' && pendingSugs.length > 0 && (
        <div className="px-5 py-2.5 border-t border-[var(--color-hairline)] flex items-center justify-end gap-2">
          {!canPersist && (
            <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-auto">
              decisions are session-local here (no run binding)
            </span>
          )}
          <button
            type="button"
            onClick={saveAll}
            disabled={batchBusy || !canPersist}
            className="text-[11px] uppercase tracking-[0.06em] font-medium rounded-sm h-7 px-3 text-[var(--color-on-accent)] bg-[var(--color-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-bg-surface)] disabled:opacity-50"
          >
            {batchBusy ? 'Saving…' : `Save ${pendingSugs.length} decision${pendingSugs.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {view && view.matched_count > 0 && (
        <div className="px-5 py-2.5 border-t border-[var(--color-hairline)] flex items-baseline justify-between gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            Overlay preview · {view.matched_count} matched
          </span>
          <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
            Σ|Δ| {view.gross_abs_delta_rcv} — preview only, not a payable · not applied to the reconciliation
          </span>
        </div>
      )}
    </div>
  );
}

// ── U1 · Reconciliation readiness band (review-completeness gate) ──
// Projects property_readiness.PropertyReadinessEvaluator onto the workbench: the score/status rollup
// + the per-check detail. This is NOT a judgement of the deterministic match — it's "how much
// human-review work remains before this reconciliation is settlement-ready". A passed check recedes
// (verified/hueless); a failed non-blocking check is needs-human (orange); a failed BLOCKING check is
// a blocker (the one red). Score renders honestly even at 0 (most review items simply unworked yet).
function readinessStatusState(status: string): 'verified' | 'needs-human' | 'blocker' {
  if (status === 'ready') return 'verified';
  if (status === 'blocked') return 'blocker';
  return 'needs-human'; // 'partial' (and any unknown) → a human must keep working the queue
}

function humanizeCheckName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function CheckStateDot({ check }: { check: ReadinessCheck }) {
  // passed → verified (hueless, receding); failed+blocking → blocker (red); failed → needs-human (orange).
  const color = check.passed
    ? 'var(--color-verified)'
    : check.blocking
      ? 'var(--color-blocker)'
      : 'var(--color-needs-human)';
  const label = check.passed ? 'passed' : check.blocking ? 'blocking gap' : 'needs review';
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
      style={{ backgroundColor: color }}
      aria-label={label}
      title={label}
    />
  );
}

function ReadinessBand({ readiness }: { readiness: Readiness }) {
  const checks = readiness.checks ?? [];
  const passedN = checks.filter((c) => c.passed).length;
  const remaining = checks.length - passedN;
  const blockingFails = checks.filter((c) => !c.passed && c.blocking).length;
  const state = readinessStatusState(readiness.status);
  const scoreText = Number.isFinite(readiness.score) ? Math.round(readiness.score) : '—';
  return (
    <div className="border-b border-[var(--color-border)]">
      {/* header: label · score · status chip */}
      <div className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-muted)]">
            Reconciliation Readiness
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5 leading-tight max-w-[26rem]">
            Review-completeness gate — how much human review remains, not a measure of match accuracy.
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-baseline gap-1">
            <span className="font-mono text-2xl tabular-nums text-[var(--color-text-primary)]" style={{ fontWeight: 500 }}>
              {scoreText}
            </span>
            <span className="text-[11px] text-[var(--color-text-muted)] font-mono">/ 100</span>
          </div>
          <StateChip state={state} text={readiness.status.toUpperCase()} />
        </div>
      </div>
      {/* per-check list */}
      {checks.length > 0 && (
        <div className="px-5 pb-3 pt-0.5 grid gap-1.5">
          {/* the load-bearing "none blocking" phrase: it's why PARTIAL-at-0 reads as work QUEUED,
              not broken. If a blocking check fails, the clause turns red instead. */}
          <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            {passedN} of {checks.length} checks clear · {remaining} remaining ·{' '}
            {blockingFails > 0 ? (
              <span className="text-[var(--color-blocker)]">
                {blockingFails} blocking failure{blockingFails === 1 ? '' : 's'}
              </span>
            ) : (
              'none blocking'
            )}
          </div>
          {checks.map((c) => {
            const pct = c.metric != null ? Math.max(0, Math.min(100, Math.round(c.metric * 100))) : null;
            return (
              <div key={c.name} className="flex items-baseline gap-2">
                <span className="pt-1"><CheckStateDot check={c} /></span>
                <span className="text-[11px] text-[var(--color-text-secondary)] min-w-[9.5rem]">
                  {humanizeCheckName(c.name)}
                  {c.blocking && !c.passed && (
                    <span className="ml-1 text-[9px] uppercase tracking-[0.06em] text-[var(--color-blocker)]">blocking</span>
                  )}
                </span>
                <span className="text-[11px] text-[var(--color-text-muted)] flex-1 min-w-0 truncate" title={c.detail}>
                  {c.detail}
                </span>
                {pct != null && (
                  <span className="inline-flex items-center gap-1.5 shrink-0">
                    <span className="inline-block h-[3px] w-14 rounded-sm bg-[var(--color-hairline)] overflow-hidden align-middle">
                      <span className="block h-full bg-[var(--color-text-muted)]" style={{ width: `${pct}%`, opacity: 0.6 }} />
                    </span>
                    <span className="text-[10px] font-mono tabular-nums text-[var(--color-text-muted)] w-8 text-right">{pct}%</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Component ──
interface Props {
  reconciliation: PropertyReconciliation | null;
  loading?: boolean;
}

const ReconciliationWorkbench: React.FC<Props> = ({ reconciliation, loading }) => {
  const [f, setF] = useState<Filters>({
    status: 'all',
    cascade: EMPTY_CASCADE,
    coverage: 'ALL',
    matchType: 'ALL',
    activity: 'ALL',
    search: '',
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAuto, setShowAuto] = useState(false); // auto-accepted band collapsed by default
  const [expandedLanes, setExpandedLanes] = useState<Set<string>>(new Set()); // U2: per-lane "show all"

  const summary: Summary | undefined = reconciliation?.summary ?? undefined;
  const meta: Meta | undefined = reconciliation?._meta ?? undefined;
  const lineItems = reconciliation?.line_items ?? {};
  const matches = reconciliation?.matches_detail ?? [];
  const variances = reconciliation?.variances_detail ?? [];
  const allLines = useMemo(() => Object.values(lineItems), [lineItems]);

  // Scope variances → resolved to their one-sided line (the ONE-SIDED section).
  const scopeVars = useMemo(
    () =>
      variances
        .filter((v) => v.category === 'scope')
        .slice()
        .sort((a, b) => a.materiality_rank - b.materiality_rank),
    [variances],
  );
  const monetaryVars = useMemo(
    () =>
      variances
        // price + quantity are dollar variances; exclude scope (shown as one-sided lines).
        // Defensive `unit !== 'base_units'`: a base-unit amount is NOT dollars — never let one
        // render through <FinancialValue> with a `$`. (Dead on the current contract, where all
        // monetary categories carry unit 'dollars'; the guard is correct-by-construction.)
        .filter((v) => v.category !== 'scope' && v.unit !== 'base_units')
        .slice()
        .sort((a, b) => a.materiality_rank - b.materiality_rank),
    [variances],
  );

  // match_id → the referencing variance of lowest (most material) rank (Decision #4:
  // "significant" is contract-driven, never client money-math).
  const varByMatch = useMemo(() => {
    const map = new Map<string, VarianceDetail>();
    for (const v of variances) {
      for (const df of v.evidence?.derived_from ?? []) {
        if (typeof df === 'string' && df.startsWith('match')) {
          const prev = map.get(df);
          if (!prev || v.materiality_rank < prev.materiality_rank) map.set(df, v);
        }
      }
    }
    return map;
  }, [variances]);

  // ── Cascade option lists, constrained top-down by the current selection ──
  const cascadeVisible = useMemo(() => {
    return CASCADE.map((tier) => {
      // A tier's options honor every HIGHER tier's current selection (top-down constraint).
      const higher = CASCADE.slice(0, CASCADE.indexOf(tier));
      const counts = new Map<string, number>();
      for (const l of allLines) {
        let ok = true;
        for (const h of higher) {
          const sel = f.cascade[h.key];
          if (sel !== 'ALL' && (h.get(l) ?? '') !== sel) { ok = false; break; }
        }
        if (!ok) continue;
        const raw = tier.get(l);
        if (raw == null || raw === '') continue;
        counts.set(String(raw), (counts.get(String(raw)) ?? 0) + 1);
      }
      const options = Array.from(counts.entries())
        .map(([raw, count]) => ({ raw, label: humanizeLoc(raw), count }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return { tier, options };
    }).filter((t) => t.options.length > 0); // auto-hide a tier with no data on either side
  }, [allLines, f.cascade]);

  // ── Data-driven refine dimensions (a chip group renders ONLY when it actually groups:
  // ≥2 distinct values and not degenerate one-per-line). This is what suppresses the
  // code-echo `category` while surfacing the real `actions` activity set. ──
  const coverageOpts = useMemo(() => distinctChip(allLines, (l) => (l.coverage_section ? [l.coverage_section] : [])), [allLines]);
  const activityOpts = useMemo(() => distinctChip(allLines, (l) => l.actions ?? []), [allLines]);
  const matchTypeOpts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of matches) counts.set(m.match_type, (counts.get(m.match_type) ?? 0) + 1);
    return Array.from(counts.entries())
      .map(([raw, count]) => ({ raw, label: raw.replace(/_/g, ' '), count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [matches]);

  // ── Predicates ──
  const lineMatchesLoc = (l: LineSnapshot): boolean => {
    for (const tier of CASCADE) {
      const sel = f.cascade[tier.key];
      if (sel !== 'ALL' && (tier.get(l) ?? '') !== sel) return false;
    }
    if (f.coverage !== 'ALL' && (l.coverage_section ?? '') !== f.coverage) return false;
    if (f.activity !== 'ALL' && !(l.actions ?? []).includes(f.activity)) return false;
    return true;
  };
  const lineMatchesSearch = (l: LineSnapshot): boolean => {
    const q = f.search.trim().toLowerCase();
    if (!q) return true;
    return (l.description ?? '').toLowerCase().includes(q) || (l.source_code ?? '').toLowerCase().includes(q);
  };

  // ── Filtered matched pairs ──
  const visibleMatches = useMemo(() => {
    if (f.status === 'one-sided') return [] as MatchDetail[];
    let list = matches.filter((m) => {
      const lines = [...m.a_lines, ...m.b_lines];
      if (!lines.some(lineMatchesLoc)) return false;
      if (!lines.some(lineMatchesSearch)) return false;
      if (f.matchType !== 'ALL' && m.match_type !== f.matchType) return false;
      // NEEDS-REVIEW filter routes by the auto-reconciliation status (the WTFK router), not the raw
      // line requires_review flag — an auto-accepted pair leaves the review pile.
      if (f.status === 'needs-review' && isAutoApproved(m)) return false;
      return true;
    });
    list = list.slice().sort((a, b) => {
      const ar = a.requires_review ? 0 : 1;
      const br = b.requires_review ? 0 : 1;
      if (ar !== br) return ar - br;
      const av = varByMatch.get(a.match_id ?? '');
      const bv = varByMatch.get(b.match_id ?? '');
      if (!!av !== !!bv) return av ? -1 : 1;
      if (av && bv && av.materiality_rank !== bv.materiality_rank) return av.materiality_rank - bv.materiality_rank;
      const ad = Math.abs(pairDeltaCents(a.a_lines, a.b_lines) ?? 0);
      const bd = Math.abs(pairDeltaCents(b.a_lines, b.b_lines) ?? 0);
      if (ad !== bd) return bd - ad;
      return (a.a_lines[0]?.description ?? '').localeCompare(b.a_lines[0]?.description ?? '');
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, f, varByMatch]);

  // ── WTFK ROUTER split: needs-review pairs are the main queue; auto-accepted collapse behind a
  // band. Nothing is auto-hidden without a stamp — a payload without router status routes to review. ──
  const reviewMatches = useMemo(() => visibleMatches.filter((m) => !isAutoApproved(m)), [visibleMatches]);
  const autoMatches = useMemo(() => visibleMatches.filter(isAutoApproved), [visibleMatches]);
  // Unfiltered router tallies for the status strip / band headers.
  const autoTotal = useMemo(() => matches.filter(isAutoApproved).length, [matches]);
  const reviewTotal = useMemo(() => matches.filter((m) => !isAutoApproved(m)).length, [matches]);

  // ── Filtered one-sided lines (scope variances resolved to their line) ──
  const visibleOneSided = useMemo(() => {
    if (f.status === 'matched') return [] as Array<{ v: VarianceDetail; line: LineSnapshot | undefined }>;
    return scopeVars
      .map((v) => ({ v, line: lineItems[v.evidence?.line_items?.[0] ?? ''] }))
      .filter(({ v, line }) => {
        if (f.matchType !== 'ALL') return false; // one-sided lines have no match tier
        if (f.status === 'needs-review' && !v.requires_review) return false;
        if (!line) return f.search.trim() === '' && f.coverage === 'ALL' && f.activity === 'ALL' && Object.values(f.cascade).every((x) => x === 'ALL');
        return lineMatchesLoc(line) && lineMatchesSearch(line);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeVars, lineItems, f]);

  // ── U2 · group the visible one-sided lines by coverage lane, ordered by |$| subtotal desc ──
  // (roof is the load-bearing lane — the real carrier-vs-expert story is WHERE scope was allocated).
  // Rows within a lane keep their materiality order (visibleOneSided is already rank-sorted).
  const oneSidedByLane = useMemo(() => {
    const groups = new Map<string, Array<{ v: VarianceDetail; line: LineSnapshot | undefined }>>();
    for (const row of visibleOneSided) {
      const lane = row.v.coverage_lane || 'unknown';
      const arr = groups.get(lane) ?? [];
      arr.push(row);
      groups.set(lane, arr);
    }
    return Array.from(groups.entries())
      .map(([lane, rows]) => ({ lane, rows, cents: laneAbsCents(rows.map((r) => r.v)) }))
      .sort((a, b) => {
        // 'unknown' is the to-do pile, not a coverage lane — pin it LAST regardless of $ (K3 gate).
        const au = a.lane === 'unknown' ? 1 : 0;
        const bu = b.lane === 'unknown' ? 1 : 0;
        if (au !== bu) return au - bu;
        const av = a.cents ?? -1;
        const bv = b.cents ?? -1;
        if (av !== bv) return bv - av; // primary: $ subtotal desc
        return LANE_ORDER.indexOf(a.lane) - LANE_ORDER.indexOf(b.lane); // stable tie-break
      });
  }, [visibleOneSided]);
  // If NO scope variance carries a coverage lane (legacy payload), fall back to the flat list.
  const hasLaneData = useMemo(() => scopeVars.some((v) => !!v.coverage_lane), [scopeVars]);
  const LANE_ROW_CAP = 8; // top-N most-material rows per lane; the rest behind a per-lane expander

  // ── Filtered monetary variance findings (price + quantity; scope shown as one-sided) ──
  const visibleMonetaryVars = useMemo(() => {
    if (f.status === 'one-sided' || f.status === 'matched') return [] as VarianceDetail[];
    return monetaryVars.filter((v) => {
      if (f.status === 'needs-review' && !v.requires_review) return false;
      const line = lineItems[v.evidence?.line_items?.[0] ?? ''];
      if (!line) return f.search.trim() === '' && f.coverage === 'ALL' && f.activity === 'ALL' && Object.values(f.cascade).every((x) => x === 'ALL');
      return lineMatchesLoc(line) && lineMatchesSearch(line);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monetaryVars, lineItems, f]);

  // ── Keyword chips: from hierarchy-visible lines (before search), hierarchy names excluded ──
  const keywordChips = useMemo(() => {
    const hierExclude = new Set<string>();
    for (const l of allLines) {
      for (const raw of [l.building, l.location_unit, l.room, l.coverage_section]) {
        for (const part of humanizeLoc(raw).split(' ')) {
          const n = normToken(part);
          if (n) hierExclude.add(n);
        }
      }
    }
    const locVisible = allLines.filter((l) => {
      for (const tier of CASCADE) {
        const sel = f.cascade[tier.key];
        if (sel !== 'ALL' && (tier.get(l) ?? '') !== sel) return false;
      }
      if (f.coverage !== 'ALL' && (l.coverage_section ?? '') !== f.coverage) return false;
      return true;
    });
    return topKeywords(locVisible.map((l) => l.description ?? ''), hierExclude, 6);
  }, [allLines, f.cascade, f.coverage]);

  const setCascade = (key: CascadeKey, value: string) => {
    setF((prev) => {
      const next = { ...prev.cascade, [key]: value };
      // Changing a higher tier resets all lower tiers to ALL (top-down only).
      const idx = CASCADE.findIndex((t) => t.key === key);
      for (let i = idx + 1; i < CASCADE.length; i++) next[CASCADE[i].key] = 'ALL';
      return { ...prev, cascade: next };
    });
  };
  const clearFilters = () =>
    setF({ status: 'all', cascade: EMPTY_CASCADE, coverage: 'ALL', matchType: 'ALL', activity: 'ALL', search: '' });
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Loading ──
  if (loading) {
    return (
      <div className={CARD_FRAME}>
        <div className="px-5 h-12 flex items-center border-b border-[var(--color-border)]">
          <span className={SECTION_HEADER}>Line-Item Reconciliation</span>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="h-3 rounded-sm bg-[var(--color-bg-elevated)] w-[60%]" />
          <div className="h-3 rounded-sm bg-[var(--color-bg-elevated)] w-[80%]" />
          <p className="text-[11px] text-[var(--color-text-muted)] font-mono mt-2">LOADING RECONCILIATION…</p>
        </div>
      </div>
    );
  }

  // ── Empty / 404 ──
  if (!reconciliation) {
    return (
      <HonestState
        tier="no-input"
        scale="panel"
        label="No contractor/adjuster reconciliation on file"
        body="When a carrier estimate and an expert estimate are both on file for this engagement, the deterministic line-item match renders here as a walkable workbench."
      />
    );
  }

  const matchCount = reconciliation.match_count ?? summary?.matches ?? matches.length;
  const scopeCount = reconciliation.scope_variance_count ?? scopeVars.length;
  // "Significant" is RESERVED for genuinely material pairs (Decision #4 refined): since the
  // engine emits a price variance for EVERY nonzero delta, "referenced by a variance" tags
  // ~every pair and means nothing. Gate the collapsed tag to the top quartile of variances by
  // the engine's own materiality_rank (min 3). The referencing variance's full context
  // (share_of_gross, root_cause) still shows in the EXPANDED panel for any referenced pair.
  const varianceCount = reconciliation.variance_count ?? variances.length;
  const sigThreshold = Math.max(3, Math.ceil(varianceCount * 0.25));
  const anyFilter =
    f.status !== 'all' || f.coverage !== 'ALL' || f.matchType !== 'ALL' || f.activity !== 'ALL' ||
    f.search.trim() !== '' || Object.values(f.cascade).some((x) => x !== 'ALL');

  const engineVocab = [
    Object.keys(reconciliation.matches_by_type ?? {}).map((k) => k.replace(/_DESCRIPTION|_/gi, (m2) => (m2 === '_' ? ' ' : ''))).join(' + ').toLowerCase() || 'exact',
    reconciliation.semantic_enabled ? 'semantic on' : 'semantic off',
  ].join(' · ');

  // ── Status strip cell ──
  const StatusCell = ({
    id, label, count, dot,
  }: {
    id: StatusFilter;
    label: string;
    count: number;
    dot: React.ReactNode;
  }) => {
    const active = f.status === id;
    return (
      <button
        type="button"
        onClick={() => setF((p) => ({ ...p, status: active ? 'all' : id }))}
        aria-pressed={active}
        className={[
          'flex items-center gap-2 px-3 h-9 rounded-sm border transition-colors',
          active
            ? 'border-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)]'
            : 'border-transparent hover:border-[var(--color-hairline)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
        ].join(' ')}
      >
        {dot}
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">{label}</span>
        <span className="font-mono text-[15px] tabular-nums text-[var(--color-text-primary)]" style={{ fontWeight: 500 }}>
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className={CARD_FRAME}>
      {/* ── 1 · Header ── */}
      <div className="px-5 py-3 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-serif text-lg font-normal text-[var(--color-text-primary)]">
            Line-Item Reconciliation
          </h2>
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
            read-only · matcher walk
          </span>
        </div>
        {meta && (
          <div className="mt-1 flex gap-4 flex-wrap text-[11px] text-[var(--color-text-muted)] font-mono">
            <span>CARRIER {meta.carrier_estimate_id} × EXPERT {meta.expert_estimate_id}</span>
            {meta.run_at && <span>RUN {formatRunAt(meta.run_at)}</span>}
            {meta.input_hash && <span>HASH {meta.input_hash.slice(0, 8)}</span>}
            <span>ENGINE {engineVocab}</span>
          </div>
        )}
      </div>

      {/* ── 1a · Reconciliation readiness (BLUF verdict band — the verdict frames the evidence
              below it; renders nothing when the payload carries no readiness, e.g. legacy blob) ── */}
      {reconciliation.readiness && <ReadinessBand readiness={reconciliation.readiness} />}

      {/* ── 2 · Status strip ── */}
      <div className="px-4 py-2 border-b border-[var(--color-border)] flex items-center justify-between gap-4 flex-wrap bg-[var(--color-bg-surface)]">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusCell
            id="matched"
            label={anyFilter ? `matched · ${visibleMatches.length} of` : 'matched'}
            count={matchCount}
            dot={<span className="inline-block w-2 h-2 rounded-full bg-[var(--color-text-primary)]" style={{ opacity: 0.5 }} aria-hidden />}
          />
          <StatusCell
            id="one-sided"
            label={anyFilter ? `one-sided · ${visibleOneSided.length} of` : 'one-sided'}
            count={scopeCount}
            dot={<span className="inline-block w-2 h-2 rounded-full border border-[var(--color-text-secondary)]" aria-hidden />}
          />
          <StatusCell
            id="needs-review"
            label={anyFilter ? `needs review · ${reviewMatches.length} of` : 'needs review'}
            count={reviewTotal}
            dot={<span className="inline-block w-2 h-2 rounded-full bg-[var(--color-needs-human-text)]" aria-hidden />}
          />
          {autoTotal > 0 && (
            <div className="flex items-center gap-2 px-3 h-9">
              <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-text-primary)]" style={{ opacity: 0.35 }} aria-hidden />
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">auto-accepted</span>
              <span className="font-mono text-[15px] tabular-nums text-[var(--color-text-secondary)]">{autoTotal}</span>
            </div>
          )}
        </div>
        {summary && (
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">NET</span>
              {/* Bind the raw SIGNED engine string (not net_dollar_variance_display): identical
                  parenthesized text for a nonzero net, but a true 0 renders FinancialValue's
                  verified-zero em-dash instead of the engine's literal "—" via the unnormalized
                  path. Same emitter, correct zero semantics. */}
              <FinancialValue value={summary.net_dollar_variance} size="lg" cents />
              <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">GROSS</span>
              <FinancialValue value={summary.gross_dollar_variance} size="lg" cents />
            </div>
            <span className="text-[10px] text-[var(--color-text-muted)] mt-0.5 max-w-[22rem] text-right leading-tight">
              Net is the engine's signed sum of all variances — not the difference between estimate totals.
            </span>
          </div>
        )}
      </div>

      {/* ── 2b · Property payable election (Step C — fixture only; renders nothing on gold) ── */}
      <PropertyElectionBand reconciliation={reconciliation} />

      {/* ── 2c · Property cap confirmation (Slice 2 — parser proposes; a human confirms the LIVE
              limit/deductible. Renders nothing when GET /policy-cap 404s / no property dimension) ── */}
      <PropertyCapConfirmCard reconciliation={reconciliation} />

      {/* ── 3 · Filter bar ── */}
      <div className="px-5 py-3 border-b border-[var(--color-border)] space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          {cascadeVisible.map(({ tier, options }) => (
            <CascadeSelect
              key={tier.key}
              label={tier.label}
              value={f.cascade[tier.key]}
              options={options}
              onChange={(v) => setCascade(tier.key, v)}
            />
          ))}
          <label className="flex flex-col gap-1 flex-1 min-w-[12rem]">
            <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">Search</span>
            <input
              type="text"
              value={f.search}
              onChange={(e) => setF((p) => ({ ...p, search: e.target.value }))}
              placeholder="description or code…"
              className="text-[12px] bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-sm px-2 h-8 text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
            />
          </label>
          {anyFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] h-8 px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] rounded-sm"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Data-driven REFINE chips — each group renders only when it actually groups. */}
        {(coverageOpts.length > 0 || activityOpts.length > 0 || matchTypeOpts.length > 1) && (
          <div className="flex items-center gap-x-5 gap-y-2 flex-wrap pt-0.5">
            {matchTypeOpts.length > 1 && (
              <ChipGroup label="Match" value={f.matchType} options={matchTypeOpts} onChange={(v) => setF((p) => ({ ...p, matchType: v }))} />
            )}
            {coverageOpts.length > 0 && (
              <ChipGroup label="Coverage" value={f.coverage} options={coverageOpts} onChange={(v) => setF((p) => ({ ...p, coverage: v }))} />
            )}
            {activityOpts.length > 0 && (
              <ChipGroup label="Activity" value={f.activity} options={activityOpts} onChange={(v) => setF((p) => ({ ...p, activity: v }))} />
            )}
          </div>
        )}

        {/* Keyword chips (click → set search) */}
        {keywordChips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mr-0.5">Keywords</span>
            {keywordChips.map((k) => {
              const active = f.search.trim().toLowerCase() === k.token;
              return (
                <button
                  key={k.token}
                  type="button"
                  onClick={() => setF((p) => ({ ...p, search: active ? '' : k.token }))}
                  className={[
                    'text-[10px] font-mono rounded-sm px-1.5 h-6 border',
                    active
                      ? 'border-[var(--color-text-secondary)] text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)]'
                      : 'border-[var(--color-hairline)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
                  ].join(' ')}
                >
                  {k.token} <span className="text-[var(--color-text-muted)]">×{k.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 4 · Matched pairs — WTFK router: auto-accepted collapse off the queue; needs-review is the queue ── */}
      {f.status !== 'one-sided' && (
        <>
          {/* Auto-accepted band: exact 0% / ≤5% / ≥0.9 fuzzy (no qty-price fight). Collapsed, count in
              the header, OFF the main queue. Accepted as PAIRS — the payable still follows election. */}
          {autoMatches.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowAuto((s) => !s)}
                aria-expanded={showAuto}
                className="w-full px-5 h-9 flex items-center justify-between border-b border-[var(--color-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
              >
                <span className="flex items-baseline gap-2 min-w-0">
                  <span className={SECTION_HEADER}>AUTO-ACCEPTED · {autoMatches.length}</span>
                  <span className="text-[10px] text-[var(--color-text-muted)] font-mono truncate">
                    exact · ≤5% · high-confidence
                  </span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)]">
                  {showAuto ? 'Hide ▴' : 'Show ▾'}
                </span>
              </button>
              {showAuto &&
                autoMatches.map((m, i) => (
                  <PairCard
                    key={m.match_id || `a-${i}`}
                    match={m}
                    expanded={expanded.has(m.match_id || `a-${i}`)}
                    onToggle={() => toggleExpand(m.match_id || `a-${i}`)}
                    refVar={varByMatch.get(m.match_id ?? '')}
                    sigThreshold={sigThreshold}
                    meta={meta}
                  />
                ))}
            </>
          )}

          {/* Needs-review queue — the default view */}
          <div className="px-5 h-9 flex items-center justify-between border-b border-[var(--color-hairline)]">
            <span className={SECTION_HEADER}>
              NEEDS REVIEW · {anyFilter ? `${reviewMatches.length} of ${reviewTotal}` : reviewTotal}
            </span>
            <span className="text-[10px] text-[var(--color-text-muted)] font-mono uppercase tracking-[0.06em]">
              carrier − expert Δ
            </span>
          </div>
          {reviewMatches.length === 0 ? (
            <div className="px-5 py-4 text-[11px] text-[var(--color-text-muted)] font-mono">
              {visibleMatches.length === 0
                ? 'no matched pairs in view — adjust the filters above'
                : '✓ no matched pairs need review — all auto-accepted'}
            </div>
          ) : (
            reviewMatches.map((m, i) => (
              <PairCard
                key={m.match_id || `r-${i}`}
                match={m}
                expanded={expanded.has(m.match_id || `r-${i}`)}
                onToggle={() => toggleExpand(m.match_id || `r-${i}`)}
                refVar={varByMatch.get(m.match_id ?? '')}
                sigThreshold={sigThreshold}
                meta={meta}
              />
            ))
          )}
        </>
      )}

      {/* ── 5 · One-sided lines — grouped by coverage lane (U2), $ subtotal desc, unknown pinned last ── */}
      {f.status !== 'matched' && visibleOneSided.length > 0 && (
        <>
          {(() => {
            const respUnknownN = visibleOneSided.filter((r) => r.v.responsibility === 'unknown').length;
            return (
              <div className="px-5 h-9 flex items-center justify-between border-t border-b border-[var(--color-hairline)]">
                <span className={SECTION_HEADER}>
                  ONE-SIDED LINES · {anyFilter ? `${visibleOneSided.length} of ${scopeCount}` : scopeCount}
                </span>
                <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
                  {hasLaneData ? 'by coverage lane' : 'present on one estimate only'}
                  {respUnknownN > 0 && ` · ${respUnknownN} resp undetermined`}
                </span>
              </div>
            );
          })()}
          {/* Legacy payload (no lane on any scope variance) → flat list, byte-for-byte prior behavior. */}
          {!hasLaneData
            ? visibleOneSided.map(({ v, line }, i) => (
                <OneSidedCard key={v.variance_id || `s-${i}`} variance={v} line={line} />
              ))
            : oneSidedByLane.map(({ lane, rows, cents }) => {
                const laneOpen = expandedLanes.has(lane);
                const shown = laneOpen ? rows : rows.slice(0, LANE_ROW_CAP);
                const hidden = rows.length - shown.length;
                const isUnknown = lane === 'unknown';
                const carrierN = rows.filter((r) => r.v.attributed_to === 'estimate_A').length;
                const expertN = rows.filter((r) => r.v.attributed_to === 'estimate_B').length;
                return (
                  <div key={lane}>
                    {/* lane sub-header: label · count · side split · [UNCLASSIFIED chip] · Σ|$| subtotal.
                        Strong top rule = group boundary (ledger law); rows beneath use hairlines. */}
                    <div className="px-5 h-9 flex items-center justify-between gap-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                      <span className="flex items-baseline gap-2 min-w-0 flex-wrap">
                        <span
                          className={[
                            'text-[11px] uppercase tracking-[0.08em] font-medium',
                            isUnknown ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-text-secondary)]',
                          ].join(' ')}
                        >
                          {LANE_LABEL[lane] ?? lane}
                        </span>
                        <span className="text-[10px] text-[var(--color-text-muted)] font-mono">
                          {rows.length} {rows.length === 1 ? 'line' : 'lines'}
                          {(carrierN > 0 || expertN > 0) && ` · ${carrierN} carrier · ${expertN} expert`}
                        </span>
                        {isUnknown && <StateChip state="needs-human" text="lane undetermined" />}
                      </span>
                      <span className="flex items-baseline gap-1.5 shrink-0">
                        <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">Σ abs</span>
                        <FinancialValue value={centsToDollars(cents)} size="md" cents />
                      </span>
                    </div>
                    {shown.map(({ v, line }, i) => (
                      <OneSidedCard key={v.variance_id || `s-${lane}-${i}`} variance={v} line={line} />
                    ))}
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedLanes((prev) => {
                            const next = new Set(prev);
                            if (next.has(lane)) next.delete(lane);
                            else next.add(lane);
                            return next;
                          })
                        }
                        className="w-full px-5 py-2 text-left border-t border-[var(--color-hairline)] text-[10px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
                      >
                        {laneOpen ? 'Show fewer ▴' : `Show ${hidden} more in ${LANE_LABEL[lane] ?? lane} ▾`}
                      </button>
                    )}
                    {/* A — read-only roof sketch geometry, next to the roof one-sided lines */}
                    {lane === 'roof' && <RoofSketchPanel sketch={reconciliation.sketch_areas} />}
                  </div>
                );
              })}
        </>
      )}

      {/* ── 6 · Monetary variance findings (price + quantity) ── */}
      {f.status !== 'matched' && f.status !== 'one-sided' && visibleMonetaryVars.length > 0 && (
        <>
          <div className="px-5 h-9 flex items-center justify-between border-t border-b border-[var(--color-hairline)]">
            <span className={SECTION_HEADER}>VARIANCE FINDINGS · {visibleMonetaryVars.length}</span>
            <span className="text-[10px] text-[var(--color-text-muted)] font-mono">price + quantity · engine-ranked</span>
          </div>
          {visibleMonetaryVars.map((v, i) => {
            const line = lineItems[v.evidence?.line_items?.[0] ?? ''];
            const desc = line?.description || v.subject.filter(Boolean).join(' · ') || '';
            const crdr = v.direction === 'credit' ? 'CR' : v.direction === 'debit' ? 'DR' : '';
            return (
              <div
                key={v.variance_id || `mv-${i}`}
                className="grid grid-cols-[32px_minmax(0,1fr)_72px_140px] items-baseline gap-x-3 px-5 py-2 border-t border-[var(--color-hairline)]"
              >
                <span className="text-[10px] text-[var(--color-text-muted)] font-mono tabular-nums">
                  {String(v.materiality_rank).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] text-[var(--color-text-primary)] truncate" title={desc}>{desc}</div>
                  {v.root_cause && <div className="text-[11px] text-[var(--color-text-muted)] mt-0.5 truncate" title={v.root_cause}>{v.root_cause}</div>}
                </div>
                <Pill>{v.category}</Pill>
                <div className="flex items-center justify-end gap-1.5">
                  {v.requires_review && <ReviewDot />}
                  <FinancialValue value={v.amount} size="md" cents />
                  {crdr && <span className="text-[9px] text-[var(--color-text-muted)] font-mono">{crdr}</span>}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── S2/S3 · Suggested matches (propose-only candidate tray; Match/No-match → reversible overlay) ── */}
      <SuggestedMatchesPanel
        candidates={reconciliation.match_candidates}
        lineItems={lineItems}
        reportId={reconciliation.report_id}
        inputHash={meta?.input_hash}
        runAt={meta?.run_at}
      />

      {/* ── WTFK-D · AI suggested matches (async MiniLM clustering; separate reversible overlay) ── */}
      <AiSuggestionsPanel
        reportId={reconciliation.report_id}
        lineItems={lineItems}
        inputHash={meta?.input_hash}
        runAt={meta?.run_at}
      />

      {/* ── 7 · Provenance footer ── */}
      {meta && (
        <div className="border-t border-[var(--color-hairline)] px-5 py-3 flex gap-6 flex-wrap">
          <span className="text-[11px] text-[var(--color-text-muted)] font-mono">
            ✓ SRC · {meta.run_by} · {formatRunAt(meta.run_at)}
          </span>
          <span className="text-[11px] text-[var(--color-text-muted)] font-mono">
            {meta.carrier_estimate_id} ↔ {meta.expert_estimate_id}
          </span>
          {reconciliation.semantic_enabled === true && (
            <span className="text-[11px] text-[var(--color-text-muted)] font-mono">semantic on</span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Pair card (collapsed row + expandable side-by-side comparison) ──
function PairCard({
  match, expanded, onToggle, refVar, sigThreshold, meta,
}: {
  match: MatchDetail;
  expanded: boolean;
  onToggle: () => void;
  refVar?: VarianceDetail;
  sigThreshold: number;
  meta?: Meta;
}) {
  // Collapsed "significant" tag only for top-materiality pairs; the expanded panel still
  // shows the referencing variance's context (share_of_gross / root_cause) for any refVar.
  const showSignificant = !!refVar && refVar.materiality_rank <= sigThreshold;
  const a0 = match.a_lines[0];
  const b0 = match.b_lines[0];
  const isFuzzy = match.match_type !== 'EXACT' && match.match_type !== 'DESCRIPTION_EXACT';
  const sameDesc = !!a0 && !!b0 && a0.description === b0.description;
  const showBoth = isFuzzy && !sameDesc;
  const deltaCents = pairDeltaCents(match.a_lines, match.b_lines);
  const multiA = match.a_lines.length > 1;
  const multiB = match.b_lines.length > 1;

  // Word-diff spans (fuzzy, differing wording only).
  const diff = showBoth && a0 && b0 ? wordDiff(a0.description, b0.description) : null;
  const shared = showBoth && a0 && b0 ? sharedKeywords(a0.description, b0.description, 4) : [];

  // Token-overlap "TEXT MATCH" (presentation only — NOT the contract confidence).
  const textMatchPct = (() => {
    if (!showBoth || !a0 || !b0) return null;
    const at = new Set((a0.description || '').split(/\s+/).map(normToken).filter(Boolean));
    const bt = new Set((b0.description || '').split(/\s+/).map(normToken).filter(Boolean));
    const union = new Set([...at, ...bt]);
    if (union.size === 0) return null;
    let inter = 0;
    at.forEach((t) => { if (bt.has(t)) inter += 1; });
    return Math.round((inter / union.size) * 100);
  })();

  return (
    <div className="px-5 py-2.5 border-t border-[var(--color-hairline)]">
      {/* collapsed row */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {showBoth && diff ? (
            <div className="space-y-0.5">
              <div className="text-[13px] leading-snug">
                <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5">≈</span>
                <DiffText spans={diff.a} />
              </div>
              <div className="text-[13px] leading-snug">
                <span className="text-[10px] font-mono text-[var(--color-text-muted)] mr-1.5 opacity-0">≈</span>
                <DiffText spans={diff.b} />
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-[var(--color-text-primary)]" title={a0?.description}>
              {a0?.description}
              <span className="ml-2 text-[9px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono">identical wording</span>
            </div>
          )}
          <div className="text-[11px] text-[var(--color-text-muted)] font-mono mt-1">{breadcrumb(a0)}</div>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <TierBadge matchType={match.match_type} />
            {textMatchPct != null && <Pill title="token overlap (presentation)">≈ {textMatchPct}% text</Pill>}
            <ConfidenceMeter confidence={match.confidence} />
            {match.requires_review && (
              <span className="inline-flex items-center gap-1">
                <ReviewDot />
                <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-needs-human-text)]">review</span>
              </span>
            )}
            {showSignificant && refVar && (
              <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-text-secondary)] font-mono" style={{ fontWeight: 600 }}>
                significant #{refVar.materiality_rank}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {!multiA && !multiB ? (
            <div className="flex items-center gap-2">
              <FinancialValue value={a0?.extended_price ?? null} size="md" cents />
              <span className="text-[var(--color-text-muted)]">|</span>
              <FinancialValue value={b0?.extended_price ?? null} size="md" cents />
            </div>
          ) : null}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono">Δ</span>
            <FinancialValue value={centsToDollars(deltaCents)} size="md" cents className="font-medium" />
          </div>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] rounded-sm"
          >
            {expanded ? 'Hide ▴' : 'Details ▾'}
          </button>
        </div>
      </div>

      {/* expanded comparison */}
      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--color-hairline)] rounded-sm overflow-hidden">
          <PairPanel side="CARRIER" sublabel={meta?.carrier_estimate_id} lines={match.a_lines} diff={diff?.a} showDiff={showBoth} />
          <PairPanel side="EXPERT" sublabel={meta?.expert_estimate_id} lines={match.b_lines} diff={diff?.b} showDiff={showBoth} />
          <div className="md:col-span-2 bg-[var(--color-bg-surface)] px-4 py-3 space-y-2">
            {shared.length > 0 && (
              <div className="text-[11px] text-[var(--color-text-muted)] font-mono">
                SHARED: {shared.join(' · ')}
              </div>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <AgreeDot label="code" agree={JSON.stringify(match.a_codes ?? []) === JSON.stringify(match.b_codes ?? [])} />
              <AgreeDot label="coverage" agree={(a0?.coverage_section ?? '') === (b0?.coverage_section ?? '')} />
              <AgreeDot
                label="location"
                agree={
                  (a0?.building ?? '') === (b0?.building ?? '') &&
                  (a0?.location_unit ?? '') === (b0?.location_unit ?? '') &&
                  (a0?.room ?? '') === (b0?.room ?? '')
                }
              />
              <AgreeDot label="qty" agree={match.quantity_ok !== false} />
              <AgreeDot label="price" agree={deltaCents === 0} />
            </div>
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono">Δ carrier − expert</span>
              <FinancialValue value={centsToDollars(deltaCents)} size="md" cents className="font-medium" />
              {refVar?.share_of_gross != null && (
                // The % is share_of_gross for THIS specific variance (the more-material of a
                // pair's price/qty effects), not necessarily the whole Δ beside it — label it
                // by the variance so the two are never conflated.
                <span className="text-[11px] text-[var(--color-text-muted)] font-mono">
                  · {refVar.category} #{refVar.materiality_rank}: {(refVar.share_of_gross * 100).toFixed(1)}% of gross
                </span>
              )}
            </div>
            {refVar?.root_cause && (
              <div className="text-[11px] text-[var(--color-text-muted)]">{refVar.root_cause}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PairPanel({
  side, sublabel, lines, diff, showDiff,
}: {
  side: string;
  sublabel?: string;
  lines: LineSnapshot[];
  diff?: DiffSpan[];
  showDiff?: boolean;
}) {
  const l0 = lines[0];
  return (
    <div className="bg-[var(--color-bg-surface)] px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">{side}</span>
        {sublabel && <span className="text-[10px] text-[var(--color-text-muted)] font-mono">{sublabel}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-text-muted)] font-mono mb-1">
        CODE {l0?.source_code ? l0.source_code : '—'}
      </div>
      <div className="text-[13px] leading-snug text-[var(--color-text-primary)]">
        {showDiff && diff ? <DiffText spans={diff} /> : l0?.description}
      </div>
      <div className="text-[11px] text-[var(--color-text-muted)] font-mono mt-1">{breadcrumb(l0)}</div>
      {lines.map((l, i) => (
        <LineFinancials key={i} line={l} />
      ))}
      {lines.some((l) => l.requires_review) && (
        <div className="flex items-center gap-1 mt-2">
          <ReviewDot />
          <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--color-needs-human-text)]">line flagged for review</span>
        </div>
      )}
    </div>
  );
}

function OneSidedCard({ variance, line }: { variance: VarianceDetail; line: LineSnapshot | undefined }) {
  const tag = variance.attributed_to === 'estimate_A' ? 'CARRIER ONLY' : variance.attributed_to === 'estimate_B' ? 'EXPERT ONLY' : '';
  const desc = line?.description || variance.subject.filter(Boolean).join(' · ') || '(unlabeled line)';
  const code = line?.source_code || variance.subject.filter(Boolean)[0] || '';
  return (
    <div className="grid grid-cols-[36px_minmax(0,1fr)_auto_132px] items-baseline gap-x-3 px-5 py-2.5 border-t border-[var(--color-hairline)]">
      {/* global materiality rank (non-contiguous within a lane — honestly shows lane interleave) */}
      <span className="text-[10px] text-[var(--color-text-muted)] font-mono tabular-nums pt-0.5">
        {variance.materiality_rank != null ? String(variance.materiality_rank).padStart(2, '0') : '—'}
      </span>
      <div className="min-w-0">
        <div className="text-[13px] text-[var(--color-text-primary)] truncate" title={desc}>{desc}</div>
        <div className="text-[11px] text-[var(--color-text-muted)] font-mono mt-0.5">
          {[code, breadcrumb(line)].filter(Boolean).join(' · ')}
        </div>
      </div>
      {/* side (which estimate — bordered chip) + ownership (who pays — borderless RESP · text):
          distinct silhouettes so ownership can't read as a third side. */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        {variance.requires_review && <ReviewDot />}
        {tag && (
          <span className="text-[9px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)] border border-[var(--color-hairline)] rounded-sm px-1.5 py-px font-mono">
            {tag}
          </span>
        )}
        <OwnershipTag responsibility={variance.responsibility} basis={variance.responsibility_basis} />
      </div>
      <div className="text-right">
        <FinancialValue value={variance.abs_amount} size="md" cents />
      </div>
    </div>
  );
}

function TierBadge({ matchType }: { matchType: string }) {
  const exact = matchType === 'EXACT' || matchType === 'DESCRIPTION_EXACT';
  return (
    <span className="text-[10px] uppercase tracking-[0.08em] font-mono text-[var(--color-text-secondary)]">
      {exact ? '✓ exact' : `≈ ${matchType.replace(/_/g, ' ').toLowerCase()}`}
    </span>
  );
}

// ── helpers ──
function distinctChip(
  lines: LineSnapshot[],
  get: (l: LineSnapshot) => string[],
): Array<{ raw: string; label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const l of lines) {
    for (const v of get(l)) {
      if (v == null || v === '') continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
  }
  const total = lines.length;
  const entries = Array.from(counts.entries());
  // Render only when the dimension actually GROUPS: ≥2 distinct values, and not the
  // degenerate one-value-per-line case (e.g. code-echo `category`). This is what keeps a
  // hollow "trade" filter off the page while surfacing the real activity set.
  if (entries.length < 2 || entries.length >= total * 0.6) return [];
  return entries
    .map(([raw, count]) => ({ raw, label: raw.length <= 4 ? raw : humanizeLoc(raw), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function formatRunAt(iso: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso || '');
  return m ? `${m[1]} ${m[2]} UTC` : iso || '';
}

export default ReconciliationWorkbench;
